import React, { useEffect, useMemo } from 'react';
import InternalContainer from '../../../layout/containers/Internal';
import { Row, Tab, Tabs } from 'react-bootstrap';
import SchemeManagementTabContainer from '../SchemeManagementTabContainer/SchemeManagementTabContainer';
import MainSchemeDetailsForm from '../SchemeManagementForms/MainSchemeDetailsForm/MainSchemeDetailsForm';
import { connect } from 'react-redux';
import { change, destroy, submit } from 'redux-form';
import {
  getInitialValuesForConfigForm,
  getInitialValuesForDetailForm,
} from '../../helpers/getInitialFormValues';
import GeneralRules from '../SchemeManagementForms/GeneralDetailsForm/components/Rules/GeneralRules';
import InsurerForm from '../SchemeManagementForms/InsurerForm/InsurerForm';
import { difference } from '../../helpers/difference';
import GeneralDetailsForm from '../SchemeManagementForms/GeneralDetailsForm/GeneralDetailsForm';
import SchemeExcessForm from '../SchemeManagementForms/ExcessForm/SchemeExcessForm';
import { InternalButtonBar } from '../../../layout/components';
import { Button } from '../../../../common/components';
import { actions } from '../../redux/ProductReducer';
import BenefitsForm from '../SchemeManagementForms/BenefitsForm/BenefitsForm';
import DeclinesForm from '../SchemeManagementForms/DeclinesForm/DeclinesForm';
import SchemeNotes from '../SchemeManagementForms/SchemeNotes/SchemeNotes';
import SchemeAudit from '../SchemeManagementForms/SchemeAudit/SchemeAudit';

const MAIN_DETAILS_TAB = 1;
const BENEFITS_TAB = 2;
const DECLINES_TAB = 3;
const NOTES_TAB = 4;
const AUDIT_TAB = 5;
const CONFIG_FORM = 'ConfigForm';
const DETAIL_FORM = 'DetailForm';

const PersonalAccidentEditContainer = ({
  isViewOnly,
  updateSchemeConfiguration,
  updateScheme,
  clearSchemeDetails,
  scheme,
  change,
  destroy,
  submit,
  getSchemeDetails,
  baseUrl,
  isSubmitting,
  productType,
}) => {
  const initialConfigValues = useMemo(() => getInitialValuesForConfigForm(scheme), [
    scheme,
  ]);
  const initialDetailsValues = useMemo(
    () => getInitialValuesForDetailForm(scheme, false),
    [scheme],
  );

  useEffect(
    () => {
      if (initialConfigValues) {
        change(CONFIG_FORM, 'meta', initialConfigValues.meta);
      }
    },
    [initialConfigValues, change],
  );

  useEffect(
    () => {
      if (initialDetailsValues) {
        change(DETAIL_FORM, 'data', initialDetailsValues.data);
        change(DETAIL_FORM, 'meta', initialDetailsValues.meta);
      }
    },
    [initialDetailsValues, change],
  );

  useEffect(() => {
    return () => {
      clearSchemeDetails();
      destroy(CONFIG_FORM);
      destroy(DETAIL_FORM);
    };
  }, []);

  const updateConfigPrefix = (newPrefix) => {
    change(CONFIG_FORM, 'meta.config.prefix', newPrefix);
  };

  const updateSchemeDetails = (formValues) => {
    const schemeDetailDifferences = difference(formValues.data, scheme.data);
    const schemeConfigDifferences = difference(formValues.meta.patch, scheme.meta);
    const allValuesToUpdate = {
      data: {
        ...schemeDetailDifferences,
      },
      meta: {
        patch: {
          ...schemeConfigDifferences,
        },
      },
    };
    return Promise.resolve(updateScheme(allValuesToUpdate, scheme.data.id)).then(() => {
      submit(CONFIG_FORM);
    });
  };

  const updateSchemeConfig = (formValues) => {
    const options = formValues.meta.config.options;
    const optionsList = options?.map((option) => {
      return option.name;
    });
    const valuesToUpdate = {
      meta: {
        ...formValues.meta,
        config: {
          ...formValues.meta.config,
          parameters: {
            ...formValues.meta.config.parameters,
            options: {
              ...formValues.meta.config.parameters.options,
              enum: optionsList,
            },
          },
        },
      },
    };
    return Promise.resolve(
      updateSchemeConfiguration(valuesToUpdate, scheme.data.id),
    ).then(() => {
      getSchemeDetails(scheme.data.id);
    });
  };
  const saveAllChanges = () => {
    submit(DETAIL_FORM);
  };

  return (
    <InternalContainer title={`${isViewOnly ? 'View' : 'Edit'} Scheme`}>
      <Tabs unmountOnExit defaultActiveKey={1} id="scheme-management">
        <Tab
          eventKey={MAIN_DETAILS_TAB}
          title="Main Details"
          unmountOnExit={false} // keep first tab mounted to allow submit on both forms
        >
          <SchemeManagementTabContainer hasScheme={!!scheme}>
            <>
              <MainSchemeDetailsForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onUpdatePrefix={updateConfigPrefix}
                onSubmit={updateSchemeDetails}
              />
              <GeneralDetailsForm
                isViewOnly={isViewOnly}
                formName={CONFIG_FORM}
                onSubmit={updateSchemeConfig}
              >
                <Row className="section-item">
                  <GeneralRules isViewOnly={isViewOnly} />
                </Row>
              </GeneralDetailsForm>
              <InsurerForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />
              <SchemeExcessForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />
            </>
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={BENEFITS_TAB} title="Benefits">
          <SchemeManagementTabContainer hasScheme={!!scheme}>
            <BenefitsForm
              isViewOnly={isViewOnly}
              formName={CONFIG_FORM}
              onSubmit={updateSchemeConfig}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={DECLINES_TAB} title="Declines">
          <SchemeManagementTabContainer hasScheme={!!scheme}>
            <DeclinesForm
              isViewOnly={isViewOnly}
              formName={CONFIG_FORM}
              onSubmit={updateSchemeConfig}
              productType={productType}
            />
          </SchemeManagementTabContainer>
        </Tab>

        <Tab eventKey={NOTES_TAB} title="Notes">
          <SchemeManagementTabContainer hasScheme={!!scheme}>
            <SchemeNotes schemeId={scheme.data.id} resourceType={'schemes'} />
          </SchemeManagementTabContainer>
        </Tab>

        <Tab eventKey={AUDIT_TAB} title="Audit">
          <SchemeManagementTabContainer hasScheme={!!scheme}>
            <SchemeAudit schemeId={scheme.data.id} />
          </SchemeManagementTabContainer>
        </Tab>
        <InternalButtonBar
          buttons={[
            {
              permissions: ['scheme.view'],
              button: <Button link buttonClass="default" to={baseUrl} label={'Back'} />,
            },
            {
              permissions: ['scheme.view'],
              button: !isViewOnly ? (
                <Button
                  bsStyle="primary"
                  className="pull-right"
                  handleClick={saveAllChanges}
                >
                  {isSubmitting ? 'Saving' : 'Save Changes'}
                  {isSubmitting ? <i className="fa fa-cog fa-spin" /> : ''}
                </Button>
              ) : (
                undefined
              ),
            },
          ]}
        />
      </Tabs>
    </InternalContainer>
  );
};
export default connect(
  null,
  {
    updateScheme: actions.updateScheme,
    updateSchemeConfiguration: actions.updateSchemeConfiguration,
    getSchemeDetails: actions.getSchemeDetails,
    clearSchemeDetails: actions.clearSchemeDetails,
    change: change,
    submit: submit,
    destroy: destroy,
  },
)(PersonalAccidentEditContainer);
